import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "../styles/bp-puzzle-frame.css";

/**
 * The jigsaw (port of js/puzzle.js, "expert" difficulty): the picture is cut into `rows` x `cols`
 * tiles, shuffled so that no tile starts in its place; drag a tile onto another to swap them.
 * A tile in its right place turns to colour and is locked. Solving it calls `onSolved`.
 */

const range = (n) => Array.from({ length: n }, (_, i) => i);

/** Random order of tiles where no tile sits in its own slot. */
function shuffledOrder(count) {
  if (count < 2) return range(count);
  let order;
  do {
    order = range(count);
    for (let i = count - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
  } while (order.some((piece, slot) => piece === slot));
  return order;
}

export default function Puzzle({ image, rows, cols, onSolved }) {
  const count = rows * cols;
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  const slotRefs = useRef([]);
  const solvedRef = useRef(false);

  const [order, setOrder] = useState(() => shuffledOrder(count));
  const [size, setSize] = useState(null); // natural size of the picture, limited to the window height
  const [frameWidth, setFrameWidth] = useState(0);
  const [viewH, setViewH] = useState(window.innerHeight);
  const [drag, setDrag] = useState(null);
  const [showFull, setShowFull] = useState(false);

  // a different picture / grid starts a new game
  useEffect(() => {
    solvedRef.current = false;
    setShowFull(false);
    setDrag(null);
    setOrder(shuffledOrder(count));
  }, [image, count]);

  // size the frame like the original: natural size, but never taller than the window
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      let width = img.width;
      let height = img.height;
      if (height > window.innerHeight) {
        height = window.innerHeight;
        width = height * (img.width / img.height);
      }
      setSize({ width, height });
    };
    img.src = image;
    return () => {
      cancelled = true;
    };
  }, [image]);

  useLayoutEffect(() => {
    const measure = () => {
      setViewH(window.innerHeight);
      const el = containerRef.current;
      if (!el) return;
      const style = window.getComputedStyle(el);
      setFrameWidth(el.offsetWidth - (parseInt(style.paddingLeft, 10) || 0) - (parseInt(style.paddingRight, 10) || 0));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [size]);

  const isLocked = (slot) => order[slot] === slot;

  const slotAt = useCallback(
    (x, y, from) => {
      for (let j = 0; j < count; j++) {
        const el = slotRefs.current[j];
        if (!el || j === from || order[j] === j) continue;
        const r = el.getBoundingClientRect();
        if (x > r.left && x < r.right && y > r.top && y < r.bottom) return j;
      }
      return null;
    },
    [count, order]
  );

  const onPointerDown = (e, slot) => {
    if (solvedRef.current || isLocked(slot)) return;
    const li = e.currentTarget;
    li.setPointerCapture?.(e.pointerId);
    setDrag({
      from: slot,
      startX: e.clientX,
      startY: e.clientY,
      dx: 0,
      dy: 0,
      over: null,
      box: { left: li.offsetLeft, top: li.offsetTop, width: li.offsetWidth, height: li.offsetHeight },
    });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    setDrag({
      ...drag,
      dx: e.clientX - drag.startX,
      dy: e.clientY - drag.startY,
      over: slotAt(e.clientX, e.clientY, drag.from),
    });
  };

  const finishDrag = (e) => {
    if (!drag) return;
    const target = slotAt(e.clientX, e.clientY, drag.from);
    setDrag(null);
    if (target === null) return;

    const next = [...order];
    [next[drag.from], next[target]] = [next[target], next[drag.from]];
    setOrder(next);

    if (next.every((piece, slot) => piece === slot) && !solvedRef.current) {
      solvedRef.current = true;
      // show the finished picture for a moment, then hand over
      setTimeout(() => setShowFull(true), 300);
      setTimeout(() => onSolved && onSolved(), 1500);
    }
  };

  const tile = (piece, slot, extra = {}) => {
    const row = Math.floor(piece / cols);
    const col = piece % cols;
    return (
      <div data-position={piece + 1} data-inplace={piece === slot ? "true" : ""} {...extra}>
        <img
          src={image}
          alt=""
          draggable={false}
          style={{ position: "relative", width: `${100 * cols}%`, top: `${-100 * row}%`, left: `${-100 * col}%` }}
        />
      </div>
    );
  };

  // the picture is shown as large as fits the screen (the window height minus the header and title)
  let frameStyle;
  if (size) {
    const ratio = size.width / size.height;
    const maxH = Math.max(220, viewH - (window.innerWidth < 769 ? 360 : 330));
    const width = Math.min(frameWidth || size.width, maxH * ratio);
    frameStyle = { width: `${width}px`, height: `${width / ratio}px` };
  }

  return (
    <div id="puzzle1" ref={containerRef}>
      {size && (
        <ul className="frame no-select" ref={gridRef} style={frameStyle}>
          {order.map((piece, slot) => {
            const locked = piece === slot;
            const dragging = drag && drag.from === slot;
            const highlighted = drag && drag.over === slot;
            return (
              <li
                key={slot}
                ref={(el) => {
                  slotRefs.current[slot] = el;
                }}
                className={highlighted ? "highlight" : undefined}
                data-inplace={locked ? "true" : undefined}
                style={{
                  height: `${100 / rows}%`,
                  maxWidth: `${100 / cols}%`,
                  flex: `1 0 ${100 / cols}%`,
                  pointerEvents: locked || solvedRef.current ? "none" : undefined,
                  touchAction: locked ? undefined : "none",
                  zIndex: dragging ? 10 : undefined,
                  transform: dragging ? `translate(${drag.dx}px, ${drag.dy}px)` : undefined,
                  transition: dragging ? "none" : undefined,
                }}
                onPointerDown={(e) => onPointerDown(e, slot)}
                onPointerMove={onPointerMove}
                onPointerUp={finishDrag}
                onPointerCancel={() => setDrag(null)}
              >
                {tile(piece, slot, dragging ? { className: "highlight" } : {})}
              </li>
            );
          })}

          {/* faded copy that stays where the tile was picked up */}
          {drag && (
            <li
              style={{
                position: "absolute",
                opacity: 0.4,
                zIndex: -2,
                pointerEvents: "none",
                left: drag.box.left,
                top: drag.box.top,
                width: drag.box.width,
                height: drag.box.height,
              }}
            >
              {tile(order[drag.from], drag.from)}
            </li>
          )}

          <img
            src={image}
            alt=""
            className="full-img"
            style={showFull ? { opacity: 1, zIndex: 1 } : { opacity: 0, zIndex: -1 }}
          />
        </ul>
      )}
    </div>
  );
}
